import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';

import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

import { CampaignHubTabs, type CampaignHubTab } from '@/components/assessment-engine/CampaignHubTabs';

import { AePageHeader, AePageShell } from '@/components/assessment-engine/layout';

import { AE_MUTED, AE_BTN_PRIMARY } from '@/components/assessment-engine/layout/aeTheme';

import { assessmentEngineService } from '@/services/assessmentEngineService';
import { useAssessmentPreview } from '@/components/assessment-engine/preview';

import type { AeCampaign, AeCampaignLink, CreateCampaignInput } from '@/types/assessmentEngine';

import {

  BasicsTab,

  RespondentExperienceTab,

} from './hub/CampaignHubTabsContent';

import { InvitationsTab } from './hub/InvitationsTab';
import { ResponsesTab } from './hub/ResponsesTab';

import { ReportTab } from './SharedLinksPage';



const TAB_PARAM = 'tab';

function parseHubTab(raw: string | null): CampaignHubTab {
  if (raw === 'overview') return 'basics';
  if (raw === 'setup' || raw === 'configuration' || raw === 'distribution') return 'invitations';
  if (
    raw === 'basics' ||
    raw === 'respondent' ||
    raw === 'invitations' ||
    raw === 'responses' ||
    raw === 'report'
  ) {
    return raw;
  }
  return 'basics';
}



/** Tabbed campaign detail hub — basics, respondent experience, invitations, responses, report. */

export const CampaignDetailHub: React.FC = () => {

  const { t } = useLanguage();

  const { campaignId } = useParams<{ campaignId: string }>();

  const [searchParams, setSearchParams] = useSearchParams();

  const [campaign, setCampaign] = useState<AeCampaign | null>(null);

  const [linkInfo, setLinkInfo] = useState<AeCampaignLink | null>(null);

  const [busy, setBusy] = useState(false);
  const loadGenerationRef = useRef(0);
  const { openCampaignPreview } = useAssessmentPreview();



  const activeTab = parseHubTab(searchParams.get(TAB_PARAM));



  const setTab = (tab: CampaignHubTab) => {

    setSearchParams({ [TAB_PARAM]: tab });

  };



  const load = useCallback((): Promise<void> => {
    if (!campaignId) return Promise.resolve();

    const generation = ++loadGenerationRef.current;

    return Promise.all([
      assessmentEngineService.getCampaign(campaignId),
      assessmentEngineService.getCampaignLink(campaignId).catch(() => null),
    ])
      .then(([nextCampaign, link]) => {
        if (generation !== loadGenerationRef.current) return;
        setCampaign(nextCampaign);
        setLinkInfo(link);
      })
      .catch(() => {
        if (generation === loadGenerationRef.current) {
          toast.error(t('assessmentEngine.hub.failedLoad'));
        }
      });
  }, [campaignId, t]);



  useEffect(() => {

    load();

  }, [load]);



  const transition = async (action: 'open' | 'close' | 'reopen') => {
    if (!campaignId) return;

    setBusy(true);

    try {
      let updated: AeCampaign;
      let invitesSent = 0;
      if (action === 'open') {
        const result = await assessmentEngineService.openCampaign(campaignId);
        updated = result.campaign;
        invitesSent = result.invitesSent;
      } else if (action === 'close') {
        updated = await assessmentEngineService.closeCampaign(campaignId);
      } else {
        const result = await assessmentEngineService.reopenCampaign(campaignId);
        updated = result.campaign;
        invitesSent = result.invitesSent;
      }

      setCampaign(updated);

      if (invitesSent > 0) {
        toast.success(t('assessmentEngine.hub.invitesSentOnOpen', { count: invitesSent }));
      } else {
        toast.success(t('assessmentEngine.hub.lifecycleUpdated'));
      }

      await load();
    } catch {
      toast.error(t('assessmentEngine.hub.actionFailed'));
    } finally {
      setBusy(false);
    }
  };



  const saveConfig = async (patch: Partial<CreateCampaignInput>) => {
    if (!campaignId || !campaign) return;

    if (campaign.status === 'draft') {
      const updated = await assessmentEngineService.updateCampaign(
        campaignId,
        patch as CreateCampaignInput
      );
      setCampaign(updated);
    } else {
      const namePatch =
        typeof patch.name === 'string' && patch.name.trim()
          ? { name: patch.name.trim() }
          : {};
      if (Object.keys(namePatch).length > 0) {
        const renamed = await assessmentEngineService.updateCampaign(campaignId, namePatch);
        setCampaign(renamed);
      }
      if (patch.notification_config !== undefined) {
        const updated = await assessmentEngineService.updateCampaign(campaignId, {
          notification_config: patch.notification_config,
        });
        setCampaign(updated);
      }
      if (patch.email_verification_required !== undefined) {
        const updated = await assessmentEngineService.updateCampaign(campaignId, {
          email_verification_required: patch.email_verification_required !== false,
        });
        setCampaign(updated);
      }
      const benchmarkMin = (patch.report_defaults as Record<string, unknown> | undefined)?.[
        'benchmark_b1_min_responses'
      ];
      const showInstant = (patch.report_defaults as Record<string, unknown> | undefined)?.[
        'show_instant_individual_results'
      ];
      const reportPatch: {
        benchmark_b1_min_responses?: number;
        show_instant_individual_results?: boolean;
      } = {};
      if (benchmarkMin != null) {
        reportPatch.benchmark_b1_min_responses = Math.max(1, Math.floor(Number(benchmarkMin)));
      }
      if (typeof showInstant === 'boolean') {
        reportPatch.show_instant_individual_results = showInstant;
      }
      if (Object.keys(reportPatch).length > 0) {
        await assessmentEngineService.updateReportConfig(campaignId, reportPatch);
      }
      if (
        patch.presentation_style !== undefined ||
        patch.respondent_config !== undefined ||
        patch.distribution_mode !== undefined ||
        patch.is_anonymous !== undefined ||
        patch.branding !== undefined ||
        patch.time_period_config !== undefined
      ) {
        const updated = await assessmentEngineService.updateCampaign(campaignId, patch);
        setCampaign(updated);
      }
    }
    load();
  };



  if (!campaign || !campaignId) {

    return (

      <AePageShell>

        <p className={AE_MUTED}>{t('assessmentEngine.hub.loading')}</p>

      </AePageShell>

    );

  }



  const publicUrl = linkInfo?.public_url ?? assessmentEngineService.buildPublicUrl(campaign.public_link_token);



  return (

    <AePageShell>

      <AePageHeader

        title={campaign.name}

        breadcrumbs={[

          { label: t('assessmentEngine.breadcrumbs.engine'), to: '/assessment-engine/campaigns' },

          { label: campaign.name },

        ]}

        actions={
          <button
            type="button"
            className={AE_BTN_PRIMARY}
            onClick={() => openCampaignPreview(campaignId)}
          >
            <EyeIcon className="inline h-5 w-5 mr-1 -mt-0.5" aria-hidden />
            {t('assessmentEngine.preview.button')}
          </button>
        }

      />



      <CampaignHubTabs active={activeTab} onChange={setTab} />



      {activeTab === 'basics' && (
        <BasicsTab
          campaign={campaign}
          busy={busy}
          onTransition={transition}
          onSave={saveConfig}
        />
      )}

      {activeTab === 'respondent' && (
        <RespondentExperienceTab campaign={campaign} onSave={saveConfig} />
      )}

      {activeTab === 'invitations' && (
        <InvitationsTab campaign={campaign} publicUrl={publicUrl} onSave={saveConfig} />
      )}

      {activeTab === 'responses' && <ResponsesTab campaign={campaign} />}

      {activeTab === 'report' && <ReportTab campaignId={campaignId} />}

    </AePageShell>

  );

};


